import { isLengthUnit, type ScreenSpec } from './ppi'

/**
 * Share links.
 *
 * Same contract as the subnet calculator: the whole input lives in the URL fragment
 * (`#…`), which browsers never send to a server, so sharing a screen costs nothing
 * in privacy. The payload is versioned so a future encoding cannot break links that
 * are already in the wild.
 *
 *   #v1;1920x1080;27;in
 */
const VERSION = 'v1'

export const DEFAULT_SPEC: ScreenSpec = {
  horizontalPixels: 1920,
  verticalPixels: 1080,
  diagonal: 27,
  unit: 'in',
}

export function encodeSpec(spec: ScreenSpec): string {
  const resolution = `${spec.horizontalPixels}x${spec.verticalPixels}`
  return [VERSION, resolution, spec.diagonal, spec.unit].join(';')
}

/** Returns `null` for anything unrecognised — a bad link must land the visitor on a
 * working calculator, never on an error page. */
export function decodeSpec(payload: string): ScreenSpec | null {
  const text = payload.replace(/^#/, '')
  if (text === '') return null

  const [version, resolution, diagonalText, unitText] = text.split(';')
  if (version !== VERSION || !resolution) return null

  const [widthText, heightText] = resolution.split('x')
  const horizontalPixels = Number(widthText)
  const verticalPixels = Number(heightText)
  const diagonal = Number(diagonalText)

  if (!Number.isInteger(horizontalPixels) || horizontalPixels <= 0) return null
  if (!Number.isInteger(verticalPixels) || verticalPixels <= 0) return null
  if (!Number.isFinite(diagonal) || diagonal <= 0) return null
  if (!isLengthUnit(unitText)) return null

  return { horizontalPixels, verticalPixels, diagonal, unit: unitText }
}

/** Writes the spec into the fragment without adding a history entry per keystroke. */
export function replaceLocationSpec(spec: ScreenSpec): void {
  if (typeof window === 'undefined') return
  const next = `${window.location.pathname}${window.location.search}#${encodeSpec(spec)}`
  window.history.replaceState(null, '', next)
}

export function readLocationSpec(): ScreenSpec | null {
  if (typeof window === 'undefined') return null
  return decodeSpec(window.location.hash)
}
